import { useMutation, useQuery } from "@tanstack/react-query";

import { del, get, post, put } from "./client";
import type { ApiResponse } from "./deals";

export interface UserAccessRole {
  id: number;
  roleKey: string;
  roleName: string;
}

export interface UserAccessPersona {
  id: number;
  personaKey: string;
  personaName: string;
}

export interface UserAccessWorkstream {
  id: number;
  code: string;
  name: string;
}

export type UserCompanyAffiliation = "Target" | "Acquirer" | "PwC";

export interface UserAccessUser {
  userId: number;
  email: string;
  displayName: string;
  isActive: boolean;
  platformRoles: UserAccessRole[];
  persona: UserAccessPersona | null;
  workstreams: UserAccessWorkstream[];
  companyAffiliation?: UserCompanyAffiliation | null;
}

export interface UserAccessPagination {
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  receivedCount: number;
  generatedAt: string;
}

export interface UserAccessUsersPage {
  items: UserAccessUser[];
  pagination: UserAccessPagination;
}

export interface CreateUserAccessRequest {
  email: string;
  companyAffiliation?: UserCompanyAffiliation | null;
  platformRoleIds: number[];
  personaId?: number | null;
  workstreamIds: number[];
  deferWelcomeEmailUntilDealSubmitted?: boolean;
}

export interface UpdateUserAccessRequest {
  companyAffiliation?: UserCompanyAffiliation | null;
  platformRoleIds: number[];
  personaId?: number | null;
  workstreamIds: number[];
  deferWelcomeEmailUntilDealSubmitted?: boolean;
}

export interface UseUserAccessUsersArgs {
  pageNumber: number;
  pageSize: number;
  search?: string;
}

type EnvelopeLike = {
  data?: unknown;
  Data?: unknown;
};

function toNumericId(value: unknown): number | null {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function toNonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeWorkstream(value: unknown): UserAccessWorkstream | null {
  if (value == null || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const id = toNumericId(record.id ?? record.Id);
  if (id == null) return null;

  const code = toNonEmptyString(record.code ?? record.Code) ?? String(id);
  const name =
    toNonEmptyString(record.name ?? record.Name) ??
    toNonEmptyString(record.workstreamName ?? record.WorkstreamName) ??
    code;

  return { id, code, name };
}

function selectWorkstreamsFromEnvelope(response: ApiResponse<UserAccessWorkstream[]>): UserAccessWorkstream[] {
  const envelope = response as EnvelopeLike;
  const raw = envelope.data ?? envelope.Data;
  if (Array.isArray(raw)) {
    return raw
      .map((item) => normalizeWorkstream(item))
      .filter((item): item is UserAccessWorkstream => item !== null);
  }

  if (raw != null && typeof raw === "object") {
    const items = (raw as { items?: unknown; Items?: unknown }).items ?? (raw as { Items?: unknown }).Items;
    if (Array.isArray(items)) {
      return items
        .map((item) => normalizeWorkstream(item))
        .filter((item): item is UserAccessWorkstream => item !== null);
    }
  }

  return [];
}

const EMPTY_USERS_PAGE: UserAccessUsersPage = {
  items: [],
  pagination: {
    pageNumber: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    receivedCount: 0,
    generatedAt: new Date(0).toISOString(),
  },
};

export const userAccessQueryKeys = {
  me: ["api", "user-access", "me"] as const,
  users: (args: UseUserAccessUsersArgs) =>
    ["api", "user-access", "users", args.pageNumber, args.pageSize, args.search ?? ""] as const,
  platformRoles: ["api", "user-access", "platform-roles"] as const,
  personas: ["api", "user-access", "personas"] as const,
  workstreams: ["api", "user-access", "workstreams"] as const,
};

/** Returns the current authenticated user's platform roles and persona. */
export function useCurrentUserProfile() {
  return useQuery({
    queryKey: userAccessQueryKeys.me,
    queryFn: () => get<ApiResponse<UserAccessUser | null>>("/user-access/me"),
    select: (res) => res.data ?? null,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Whether the signed-in user holds any of the given platform roles.
 *
 * Mirrors the check `ProtectedRoute` performs for route guards, for the cases that need the same
 * answer inside a page — e.g. whether to offer a CTA that leads to a guarded route. Returns
 * `false` while the profile loads, so a transient `true` never flashes an action the user then
 * gets bounced from.
 */
export function useHasPlatformRole(roles: readonly string[]): boolean {
  const { data: profile } = useCurrentUserProfile();
  const userRoleKeys = profile?.platformRoles?.map((role) => role.roleKey) ?? [];
  return roles.some((role) => userRoleKeys.includes(role));
}

const DATA_QUALITY_HIDDEN_PERSONA_KEY = "integration_steering_committee_member_business_sponsor";

/**
 * Whether the AI Insights "Data quality" section should be shown to the signed-in user's persona.
 * Hidden for Integration Steering Committee Member/Business Sponsor; visible for everyone else
 * (including while the persona is still loading, consistent with `useHasPlatformRole`'s
 * fail-closed-vs-open choice — here we default to showing rather than hiding, since this is a
 * UX declutter, not an access control).
 */
export function useIsDataQualityVisible(): boolean {
  const { data: profile } = useCurrentUserProfile();
  return profile?.persona?.personaKey !== DATA_QUALITY_HIDDEN_PERSONA_KEY;
}

export function useUserAccessUsers(args: UseUserAccessUsersArgs) {
  return useQuery({
    queryKey: userAccessQueryKeys.users(args),
    queryFn: () =>
      get<ApiResponse<UserAccessUsersPage>>("/user-access/users", {
        params: {
          pageNumber: args.pageNumber,
          pageSize: args.pageSize,
          ...(args.search && args.search.trim().length > 0 ? { search: args.search.trim() } : {}),
        },
      }),
    select: (res) => res.data ?? EMPTY_USERS_PAGE,
    staleTime: 15 * 1000,
  });
}

export function useUserAccessPlatformRoles(enabled = true) {
  return useQuery({
    queryKey: userAccessQueryKeys.platformRoles,
    queryFn: () => get<ApiResponse<UserAccessRole[]>>("/user-access/platform-roles"),
    select: (res) => res.data ?? [],
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

export function useUserAccessPersonas(enabled = true) {
  return useQuery({
    queryKey: userAccessQueryKeys.personas,
    queryFn: () => get<ApiResponse<UserAccessPersona[]>>("/user-access/personas"),
    select: (res) => res.data ?? [],
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

export function useUserAccessWorkstreams(enabled = true) {
  return useQuery({
    queryKey: userAccessQueryKeys.workstreams,
    queryFn: () => get<ApiResponse<UserAccessWorkstream[]>>("/user-access/workstreams"),
    select: selectWorkstreamsFromEnvelope,
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

export function useCreateUserAccess() {
  return useMutation({
    mutationFn: (request: CreateUserAccessRequest) =>
      post<ApiResponse<UserAccessUser>>("/user-access/users", request),
  });
}

export function useUpdateUserAccess() {
  return useMutation({
    mutationFn: (args: { userId: number; request: UpdateUserAccessRequest }) =>
      put<ApiResponse<UserAccessUser>>(`/user-access/users/${args.userId}`, args.request),
  });
}

export function useRemoveUserAccess() {
  return useMutation({
    mutationFn: (userId: number) => del<ApiResponse<boolean>>(`/user-access/users/${userId}`),
  });
}
